import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { API_PATHS } from '../config/api-paths';
import { KoCode } from '../models/ko-code.model';
import { RejectionCodesApi } from '../tokens/rejection-codes.token';
import { FinancingProductCode, toUiProduct } from '../../shared/financing/product-gate/resolve-product';

/**
 * REST-only `RejectionCodesApi` (EM-1469, epic EM-1447 Story 2) —
 * `GET /services/embedded/api/financing/ko-codes`.
 *
 * Not cached, deliberately: unlike the rating scale, KO thresholds and wordings are edited through
 * Master Admin during the working day, and the acceptance criterion for "config over code" is that a
 * `PUT /api/ko-rules/{id}` shows up **after a refresh** with no portal change. A session cache would
 * break exactly that.
 *
 * The `?product=` value is the UI spelling (`IF|WCL|SDF`) produced by the ONE mapping in
 * `shared/financing/product-gate/resolve-product.ts` — no page hand-maps the enum here.
 *
 * Rule 09 — errors are deliberately NOT mapped here; the RFC 7807 body reaches the caller untouched
 * so the page can run it through `extractBackendErrorMessage(err, fallback)` with a translated
 * fallback.
 *
 * Rules 07 / 10 — no mock fallback, no `Mock*` sibling.
 */
@Injectable()
export class RejectionCodesRestService implements RejectionCodesApi {
  private readonly http = inject(HttpClient);

  listCodes(product: FinancingProductCode | null, language: string): Observable<KoCode[]> {
    let params = new HttpParams().set('lang', language || 'en');
    if (product) {
      params = params.set('product', toUiProduct(product));
    }
    return this.http.get<KoCode[]>(API_PATHS.FINANCING_KO_CODES, { params });
  }
}
